import React, { useState } from "react";
import { ImageContentModule } from "@/components/ImageContentModule";
import { CoverImage } from "@/components/CoverImage";
import { Accordion } from "@/components/Accordion";
import { BuyButton } from "@/features/cart/BuyButton";
import {
  SubscriptionSelector,
  SubscriptionSelectorValue,
} from "@/features/product/SubscriptionSelector";
import { Money } from "@/helpers/Money";
import { DroppsProduct, productSelect, variantSelect } from "@/store";

export interface FormattedBundle {
  title: string;
  OOSItems: boolean;
  image: {
    url: string;
    alt: string;
    height: number;
    width: number;
  };
  discountText?: string;
  spotDiscountCode?: string;
  description: string;
  price: Money;
  comparePrice: Money;
  products: DroppsProduct[];
  subscription?: {
    subDiscountCode?: string;
    price: Money;
    comparePrice: Money;
    discountText?: string;
    cta: string;
    disclaimer: string;
  };
}

export interface BundleModuleContentProps {
  className?: string;
  layout: "image-left" | "image-right";
  bundle: FormattedBundle;
}

export const BundleModuleContent = (props: BundleModuleContentProps) => {
  const { bundle, ...moduleProps } = props;
  const [subToggle, setSubToggle] = useState<SubscriptionSelectorValue>(
    bundle.subscription ? "subscription" : "one-time"
  );

  const buyButton = (
    <BuyButton
      disabled={bundle.OOSItems}
      discountCode={
        subToggle === "subscription"
          ? bundle.subscription?.subDiscountCode
          : bundle.spotDiscountCode
      }
      data-id={
        subToggle === "subscription" ? "checkout-subscription" : "checkout-spot"
      }
      sourceModule={"bundles_page"}
      items={bundle.products.map((product) => {
        const variant = productSelect.defaultVariant(product);
        return variantSelect.cartAddLineItem(variant, {
          subscription:
            subToggle === "subscription"
              ? productSelect.defaultSellingPlan(product)
              : false,
          product,
        });
      })}
    >
      {bundle.OOSItems ? "OUT OF STOCK" : "Buy Now"}
    </BuyButton>
  );

  const content = (
    <div className="space-y-6">
      <h2 className="title text-4xl">{bundle.title}</h2>
      <p className="body text-lg leading-relaxed">{bundle.description}</p>
      <div className="border-0 border-y border-solid border-primary-200">
        <Accordion
          entries={bundle.products.map((product) => {
            const variant = productSelect.defaultVariant(product);
            return {
              title: productSelect.name(product),
              content: (
                <div className="space-y-2">
                  <p className="body leading-relaxed">
                    {productSelect.highlight(product)}
                  </p>
                  <p className="body leading-relaxed">
                    <span>
                      <span className="font-bold">Scent:</span>{" "}
                      {productSelect.scent(product)}
                    </span>
                    <span className="mx-2">|</span>
                    <span>
                      <span className="font-bold">Size:</span>{" "}
                      {variantSelect.sizeFormat(variant)}
                    </span>
                  </p>
                </div>
              ),
            };
          })}
        />
      </div>

      {bundle.subscription ? (
        <div className="space-y-4">
          <SubscriptionSelector
            value={subToggle}
            onChange={(value) => {
              setSubToggle(value);
            }}
            size="sm"
            cta={
              <div className="w-full items-center justify-between space-y-4 lg:flex lg:space-x-4 lg:space-y-0">
                <div>
                  <p className="body text-sm">{bundle.subscription.cta}</p>
                  <p className="body text-sm">Change or Cancel Anytime</p>
                </div>
                <p className="body text-sm lg:text-right">
                  Ships every 4 months.
                </p>
              </div>
            }
          />
          <div className="flex w-full items-center justify-between space-x-6">
            {buyButton}
            <div className="items-center space-x-2 text-right sm:flex">
              <p className="body text-sm line-through">
                <span className="sr-only">Regular price</span>
                {subToggle == "subscription"
                  ? bundle.subscription.comparePrice.format()
                  : bundle.comparePrice.format()}
              </p>
              <p className="body text-2xl font-semibold">
                <span className="sr-only">Current price</span>
                {subToggle == "subscription"
                  ? bundle.subscription.price.format()
                  : bundle.price.format()}
              </p>
            </div>
          </div>
          {subToggle == "subscription" && (
            <p className="body text-xs">{bundle.subscription.disclaimer}</p>
          )}
        </div>
      ) : (
        <div className="flex w-full items-center justify-between space-x-4">
          <div>
            <p className="body text-2xl font-semibold">
              <span className="sr-only">Current price</span>
              {bundle.price.format()}
            </p>
            <p className="body text-sm">
              (a {bundle.comparePrice.format()} value)
            </p>
          </div>
          {buyButton}
        </div>
      )}
    </div>
  );
  return (
    <ImageContentModule
      image={
        <>
          <div className="pointer-events-none absolute right-5 top-5 flex h-28 w-28 items-center justify-center rounded-full bg-secondary text-center text-xl font-semibold uppercase text-white lg:h-36 lg:w-36 lg:text-2xl">
            {subToggle === "subscription" && bundle.subscription
              ? bundle.subscription.discountText
              : bundle.discountText}
          </div>
          <CoverImage src={bundle.image.url} />
        </>
      }
      content={content}
      {...moduleProps}
    />
  );
};
